/**
 * Stadium crowd: two looping beds (calm murmur + on-their-feet hype) mixed by a continuous intensity, a
 * brightness filter that opens as the crowd gets louder, a "DE-FENSE!" chant loop, and one-shot reactions.
 *
 *   bed ──┐
 *   hype ─┼─► bedFilter (low-pass, opens with intensity) ─┐
 *   chant ┘                                               ├─► out (crowd bus input)
 *   reactions (cheer/roar/boo/ooh/gasp) ──────────────────┘
 *
 * Intensity changes glide (setTargetAtTime), so the bridge may call `setIntensity` every frame. When the beds'
 * files are missing, a procedural murmur (filtered, slowly modulated noise) stands in so the stadium is never dead.
 * Loops stop after the intensity has been 0 for a while (no idle CPU); every node is released when it ends.
 */
import type { CrowdReaction } from './index';
import { LoopTrack } from './loop';

/** Intensity glide time constant (s). */
const GLIDE = 0.35;
/** Loops stop after this long at intensity 0 (s). */
const IDLE_STOP_S = 2;
/** Max simultaneous reaction one-shots. */
const MAX_REACTIONS = 3;
const REACTION_COOLDOWN_S = 0.35;

export const CROWD_IDS = {
  bed: 'crowd_bed',
  hype: 'crowd_bed_hype',
  chant: 'crowd_chant',
} as const;
export const reactionAssetId = (kind: CrowdReaction): string => `crowd_${kind}`;

/**
 * Bed/hype mix for an intensity. Overall loudness L rises monotonically; the hype bed takes over with an
 * equal-power crossfade (never fully: some murmur stays under the roar), so total power is monotonic too.
 */
export const crowdMix = (i: number): { bed: number; hype: number; cutoff: number } => {
  const x = Math.min(1, Math.max(0, Number.isFinite(i) ? i : 0));
  const t = Math.min(1, Math.max(0, (x - 0.3) / 0.7));
  const share = t * t * (3 - 2 * t);
  const loud = Math.min(1, x / 0.12) * (0.6 + 0.4 * x);
  const a = share * (Math.PI / 2) * 0.8;
  return {
    bed: loud * Math.cos(a),
    hype: loud * Math.sin(a),
    // 900 Hz (distant murmur) → ~16 kHz (full roar)
    cutoff: 900 * Math.pow(2, x * 4.15),
  };
};

export interface CrowdSources {
  /** Decoded buffer by asset id, or null. */
  buffer(id: string): AudioBuffer | null;
  /** Linear trim of an asset (manifest `gain`). */
  trim(id: string): number;
  /** Seam crossfade of a looping asset. */
  xfade(id: string): number | undefined;
  random(): number;
}

interface Reaction {
  src: AudioBufferSourceNode;
  gain: GainNode;
  startedAt: number;
  fading: boolean;
}

export class Crowd {
  readonly out: GainNode;
  private readonly filter: BiquadFilterNode;
  private readonly bedGain: GainNode;
  private readonly hypeGain: GainNode;
  private readonly chantGain: GainNode;
  private bed: LoopTrack | null = null;
  private hype: LoopTrack | null = null;
  private chant: LoopTrack | null = null;
  private noise: AudioBuffer | null = null;
  private usingNoise = false;
  private target = 0;
  private applied = -1;
  private zeroSince = 0;
  private chantOn = false;
  private readonly reactions: Reaction[] = [];
  private readonly lastReaction = new Map<string, number>();

  constructor(
    private readonly ctx: BaseAudioContext,
    dest: AudioNode,
    private readonly src: CrowdSources,
  ) {
    this.out = ctx.createGain();
    this.out.connect(dest);
    this.filter = ctx.createBiquadFilter();
    this.filter.type = 'lowpass';
    this.filter.Q.value = 0.5;
    this.filter.frequency.value = 900;
    this.filter.connect(this.out);
    this.bedGain = ctx.createGain();
    this.hypeGain = ctx.createGain();
    this.chantGain = ctx.createGain();
    this.bedGain.gain.value = 0;
    this.hypeGain.gain.value = 0;
    this.chantGain.gain.value = 0;
    this.bedGain.connect(this.filter);
    this.hypeGain.connect(this.filter);
    this.chantGain.connect(this.out);
  }

  get intensity(): number {
    return this.target;
  }

  setIntensity(i: number): void {
    const x = Math.min(1, Math.max(0, Number.isFinite(i) ? i : 0));
    this.target = x;
    const now = this.ctx.currentTime;
    if (x === 0 && this.zeroSince === 0) this.zeroSince = now || 1e-6;
    if (x > 0) this.zeroSince = 0;
    if (Math.abs(x - this.applied) < 0.004) return;
    this.applied = x;
    if (x > 0) this.ensureBeds(now);
    const m = crowdMix(x);
    glide(this.bedGain.gain, m.bed, now, GLIDE);
    glide(this.hypeGain.gain, m.hype, now, GLIDE);
    glide(this.filter.frequency, m.cutoff, now, GLIDE);
  }

  setChant(on: boolean): void {
    if (on === this.chantOn) return;
    this.chantOn = on;
    const now = this.ctx.currentTime;
    if (on) {
      const buf = this.src.buffer(CROWD_IDS.chant);
      if (!buf) return;
      if (!this.chant || !this.chant.playing) {
        this.chant = new LoopTrack(this.ctx, CROWD_IDS.chant, buf, this.chantGain, {
          loop: true,
          xfade: this.src.xfade(CROWD_IDS.chant) ?? 0.6,
        });
        this.chant.start(now, 0.05, this.src.trim(CROWD_IDS.chant));
      }
      glide(this.chantGain.gain, 0.8, now, 0.3);
    } else {
      glide(this.chantGain.gain, 0, now, 0.25);
      this.chant?.stop(now, 1.2);
      this.chant = null;
    }
  }

  react(kind: CrowdReaction, strength = 1): void {
    const id = reactionAssetId(kind);
    const buf = this.src.buffer(id);
    if (!buf) return;
    const now = this.ctx.currentTime;
    const last = this.lastReaction.get(id);
    if (last !== undefined && now - last < REACTION_COOLDOWN_S) return;
    this.lastReaction.set(id, now);
    // cap: fade out the oldest still-sounding reaction (it stays tracked until it ends, for dispose)
    const sounding = this.reactions.filter((x) => !x.fading);
    if (sounding.length >= MAX_REACTIONS) {
      const old = sounding[0]!;
      old.fading = true;
      const g = old.gain.gain;
      g.cancelScheduledValues(now);
      g.setValueAtTime(g.value, now);
      g.linearRampToValueAtTime(0, now + 0.25);
      try {
        old.src.stop(now + 0.27);
      } catch {
        /* already stopped */
      }
    }
    const s = Math.min(1.5, Math.max(0.05, Number.isFinite(strength) ? strength : 1));
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = 1 + (this.src.random() * 2 - 1) * 0.04;
    const gain = this.ctx.createGain();
    // a reaction swells in (crowds never start on a dime) and scales with strength
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(s * this.src.trim(id), now + 0.06);
    src.connect(gain);
    gain.connect(this.out);
    const r: Reaction = { src, gain, startedAt: now, fading: false };
    this.reactions.push(r);
    src.onended = () => {
      src.onended = null;
      src.disconnect();
      gain.disconnect();
      const i = this.reactions.indexOf(r);
      if (i >= 0) this.reactions.splice(i, 1);
    };
    src.start(now);
  }

  /** Housekeeping (~10×/s): pump loops, stop idle beds, pick up beds that finished loading. */
  pump(now: number): void {
    this.bed?.pump(now);
    this.hype?.pump(now);
    this.chant?.pump(now);
    if (this.target > 0) {
      this.ensureBeds(now);
      if (this.chantOn && !this.chant) {
        this.chantOn = false;
        this.setChant(true);
      }
    } else if (this.zeroSince > 0 && now - this.zeroSince > IDLE_STOP_S) {
      this.stopBeds(now);
    }
  }

  /** Fade every reaction out (leaving the stadium). */
  hushReactions(fade = 0.4): void {
    const now = this.ctx.currentTime;
    for (const r of this.reactions) {
      r.fading = true;
      const g = r.gain.gain;
      g.cancelScheduledValues(now);
      g.setValueAtTime(g.value, now);
      g.linearRampToValueAtTime(0, now + fade);
      try {
        r.src.stop(now + fade + 0.02);
      } catch {
        /* already stopped */
      }
    }
  }

  dispose(): void {
    this.bed?.dispose();
    this.hype?.dispose();
    this.chant?.dispose();
    this.bed = this.hype = this.chant = null;
    for (const r of this.reactions.splice(0)) {
      r.src.onended = null;
      try {
        r.src.stop();
      } catch {
        /* already stopped */
      }
      r.src.disconnect();
      r.gain.disconnect();
    }
    this.bedGain.disconnect();
    this.hypeGain.disconnect();
    this.chantGain.disconnect();
    this.filter.disconnect();
    this.out.disconnect();
  }

  private ensureBeds(now: number): void {
    const bedBuf = this.src.buffer(CROWD_IDS.bed);
    // upgrade from the procedural murmur to the real bed once it has loaded
    if (bedBuf && this.usingNoise && this.bed) {
      this.bed.stop(now, 1.5);
      this.bed = null;
      this.usingNoise = false;
    }
    if (!this.bed || !this.bed.playing) {
      if (bedBuf) {
        this.bed = new LoopTrack(this.ctx, CROWD_IDS.bed, bedBuf, this.bedGain, {
          loop: true,
          xfade: this.src.xfade(CROWD_IDS.bed) ?? 1.5,
        });
        this.bed.start(now, 1, this.src.trim(CROWD_IDS.bed));
      } else {
        this.noise ??= makeMurmur(this.ctx, this.src.random);
        this.usingNoise = true;
        this.bed = new LoopTrack(this.ctx, 'crowd_murmur', this.noise, this.bedGain, {
          loop: true,
          xfade: 0.6,
        });
        this.bed.start(now, 1);
      }
    }
    const hypeBuf = this.src.buffer(CROWD_IDS.hype);
    if (hypeBuf && (!this.hype || !this.hype.playing)) {
      this.hype = new LoopTrack(this.ctx, CROWD_IDS.hype, hypeBuf, this.hypeGain, {
        loop: true,
        xfade: this.src.xfade(CROWD_IDS.hype) ?? 1.5,
      });
      this.hype.start(now, 1, this.src.trim(CROWD_IDS.hype));
    }
  }

  private stopBeds(now: number): void {
    this.bed?.stop(now, 0.5);
    this.hype?.stop(now, 0.5);
    this.bed = this.hype = null;
    this.usingNoise = false;
    this.applied = -1;
  }
}

/** Smoothly move a param toward `value` (cancels pending automation, no zipper noise). */
export const glide = (p: AudioParam, value: number, now: number, tau: number): void => {
  p.cancelScheduledValues(now);
  p.setValueAtTime(p.value, now);
  p.setTargetAtTime(value, now, tau);
};

/**
 * Procedural stadium murmur (fallback while/if the bed file is unavailable): 4 s of stereo noise, band-limited
 * to the voice range, with slow independent swells per channel so it reads as many distant voices.
 */
export const makeMurmur = (ctx: BaseAudioContext, random: () => number): AudioBuffer => {
  const sr = ctx.sampleRate;
  const len = Math.round(sr * 4);
  const buf = ctx.createBuffer(2, len, sr);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    let lp = 0;
    let lp2 = 0;
    let hp = 0;
    const phases = [random() * 6.28, random() * 6.28, random() * 6.28];
    for (let i = 0; i < len; i++) {
      const t = i / sr;
      const white = random() * 2 - 1;
      lp += (white - lp) * 0.18; // ~1.4 kHz low-pass
      lp2 += (lp - lp2) * 0.5;
      hp += (lp2 - hp) * 0.02; // remove rumble below ~140 Hz
      const swell =
        0.65 +
        0.2 * Math.sin(2 * Math.PI * 0.5 * t + phases[0]!) +
        0.1 * Math.sin(2 * Math.PI * 1.25 * t + phases[1]!) +
        0.05 * Math.sin(2 * Math.PI * 3.5 * t + phases[2]!);
      d[i] = (lp2 - hp) * swell * 1.6;
    }
  }
  return buf;
};
